import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';

import { SOVEREIGN } from '../../../epc/utils/epcProjectVisual';
import { useRoleEditorContext } from './RoleEditorContext';
import { useRoleEditor } from './useRoleEditor';

/**
 * Sticky bottom chrome for the role editor. Lives at the bottom of the
 * scrollable editor canvas — flat bottom, hairline top border, no rounding.
 *
 * Reads:
 *   - useRoleEditor()        — submit() trigger + isSubmitting state
 *   - useRoleEditorContext() — editingRole to choose copy
 *
 * Buttons are `type="button"`. The parent shell owns the <form>; we call
 * submit() imperatively so the footer can live anywhere in the tree
 * (sticky outside the scroll container, for instance) without depending
 * on form-tag adjacency.
 */
export interface RoleEditorFooterProps {
  onClose?: () => void;
}

export default function RoleEditorFooter({ onClose }: RoleEditorFooterProps) {
  const { editingRole } = useRoleEditorContext();
  const { submit, isSubmitting } = useRoleEditor();

  const isEditing = Boolean(editingRole);
  const infoText = isEditing
    ? 'Changes will apply immediately to all users with this role.'
    : 'New role will be active immediately upon creation.';
  const infoDotColor = isEditing ? SOVEREIGN.champagne : SOVEREIGN.brand;
  const saveLabel = isSubmitting
    ? 'Saving…'
    : isEditing
      ? 'Save changes'
      : 'Create role';

  const handleSave = () => {
    if (isSubmitting) return;
    void submit();
  };

  return (
    <motion.footer
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
      className="sticky bottom-0 z-10 flex flex-col gap-3 px-6 py-4 sm:flex-row sm:items-center sm:justify-between"
      style={{
        background: SOVEREIGN.cardBg,
        borderTop: `1px solid ${SOVEREIGN.innerRule}`,
        boxShadow: `0 -1px 3px rgba(11,59,46,0.05)`,
      }}
    >
      {/* LEFT — info pill */}
      <div className="flex items-center gap-2 min-w-0">
        <span
          className="inline-block h-1.5 w-1.5 shrink-0 rounded-full"
          style={{ backgroundColor: infoDotColor }}
          aria-hidden="true"
        />
        <span
          className="truncate text-[12.5px] font-medium"
          style={{ color: SOVEREIGN.kpiCaption }}
        >
          {infoText}
        </span>
      </div>

      {/* RIGHT — actions */}
      <div className="flex items-center gap-2 sm:gap-3 justify-end">
        <motion.button
          type="button"
          onClick={onClose}
          disabled={isSubmitting}
          whileHover={!isSubmitting ? { scale: 1.02 } : undefined}
          whileTap={!isSubmitting ? { scale: 0.98 } : undefined}
          className="inline-flex items-center justify-center rounded-xl px-4 py-2.5 text-[13px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60"
          style={{
            border: `1px solid ${SOVEREIGN.cardBorder}`,
            background: SOVEREIGN.inputBg,
            color: SOVEREIGN.ink,
          }}
        >
          Cancel
        </motion.button>

        <motion.button
          type="button"
          onClick={handleSave}
          disabled={isSubmitting}
          whileHover={!isSubmitting ? { scale: 1.02 } : undefined}
          whileTap={!isSubmitting ? { scale: 0.98 } : undefined}
          className="relative inline-flex items-center justify-center gap-2 overflow-hidden rounded-xl px-5 py-2.5 text-[13px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-70"
          style={{
            background: SOVEREIGN.btn,
            boxShadow: SOVEREIGN.btnShadow,
          }}
        >
          <span className="relative flex items-center gap-2">
            {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.5} />}
            {saveLabel}
          </span>
        </motion.button>
      </div>
    </motion.footer>
  );
}
